<!--  -->
<template>
  <v-container v-if="!policyStore.policyInfoLoading" class="pa-0 pb-16 mb-16">
    <template v-for="item in policiesForCardStack" :key="item.contractId">
      <template v-if="policyStore.currentPolicy.contractId === item.contractId">
        <PolicyOverview
          ref="view_policy"
          v-if="policyStore.currentPolicy"
          :policy-data="policyStore.currentPolicy"
          :restrict-code-message="policyStore.restrictCodeMessage"
          :client-communications-phone="policyStore.clientCommunicationsPhone"
          role="tabpanel"
          aria-labelledby="my_policy_tab"
          id="policy-panel"
          tabindex="-1"
          class="mb-2"
        />

        <PaymentInformationCard
          ref="payment"
          id="payment"
          v-if="policyStore.currentPolicy.active"
          class="mb-2"
        />

        <AnnuitiesComponent v-if="policyStore.currentPolicy && hasAnnuity" class="mb-2" />

        <PolicyOwner
          ref="contact_info"
          id="contact_info"
          v-if="policyStore.currentPolicy.owner"
          :allow-edit="true"
          class="mb-2"
        />

        <BeneficiariesComponent
          ref="beneficiary"
          id="beneficiary"
          :is-restricted-policy="isRestricted"
          :is-us-policy-code="isUsPolicy"
          :is-ny-policy-code="isNyPolicy"
          :is-canada-policy-code="isCanadaPolicy"
          :is-canada-quebec="isCanadaQuebecPolicy"
          class="mb-2"
        />

        <Representative
          v-if="hasAgents"
          :agents="agents"
          :restricted="policyStore.currentPolicy.restricted"
          :life="true"
        />
      </template>
    </template>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { mapStores } from 'pinia';
import { formatDate } from '@/shared/util/formatters';
import { RouteNames } from '@/router/routeNames';
import { useGlobalStore } from '@/stores/global/global.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import type { IPolicyStorePolicy } from '@/modules/policy/store/policyStore.model';
import Representative from '@/components/Representative.vue';
import BeneficiariesComponent from './components/Beneficiaries/BeneficiariesBase.vue';
import PolicyOwner from './components/PolicyOwner/PolicyOwner.vue';
import PolicyOverview from './components/PolicyOverview/PolicyOverview.vue';
import AnnuitiesComponent from './components/Annuities/AnnuitiesComponent.vue';
import PaymentInformationCard from './components/PaymentInformation/PaymentInformation.vue';

const componentOptions: ComponentOptions = {
  name: 'PolicyDetailsTab',
  components: {
    BeneficiariesComponent,
    PolicyOwner,
    PolicyOverview,
    AnnuitiesComponent,
    Representative,
    PaymentInformationCard,
  },
  data() {
    return {
      activeTab: 1,
      activePolicyTab: 0,
    };
  },
  computed: {
    ...mapStores(useGlobalStore, usePolicyStore),
    hasAgents() {
      return this.agents?.length === 1;
    },
    agents() {
      const _agent = this.globalStore.getAgent(this.policyStore.currentPolicy?.agentId || '');
      const reps = [];
      const agent = Object.assign({}, _agent);
      agent.index = 0;
      reps.push(agent);
      return reps;
    },
    policiesForCardStack() {
      const allPolicies = this.policyStore.policies
        ?.slice(this.policyStore.selectedPolicyIndex, this.policyStore.policies.length)
        ?.reverse();
      return allPolicies.filter((policy: IPolicyStorePolicy) => !policy.restricted);
    },
    policyAnnuity() {
      return this.policyStore.currentPolicy?.details?.annuity;
    },
    isUsPolicy() {
      return this.policyStore.currentPolicy?.isUSPolicy;
    },
    isNyPolicy() {
      return this.policyStore.currentPolicy?.isNewYorkPolicy;
    },
    isCanadaPolicy() {
      return this.policyStore.currentPolicy?.isCanadaPolicy;
    },
    isCanadaQuebecPolicy() {
      return this.policyStore.currentPolicy?.isCanadaQuebecPolicy;
    },
    isPending() {
      return this.policyStore.currentPolicy?.pending;
    },
    isRestricted() {
      return this.policyStore.currentPolicy?.restricted;
    },
    formattedPaymentDueDate() {
      return formatDate(this.policyStore.currentPolicy?.nextPaymentDueDate, 'MMM d, yyyy');
    },
    hasAnnuity() {
      return this.policyStore.currentPolicy?.paynowInfo?.hasAnnuity;
    },
  },
  methods: {
    cardClass(index: number) {
      const reverseIndex = this.policiesForCardStack.length - (index + 1);
      if (reverseIndex >= 3) {
        return 'last-cards';
      }
      return `card-${reverseIndex}${
        index === this.policyStore.selectedPolicyIndex ? ' policy-active' : ''
      }`;
    },
  },
  watch: {
    ['policyStore.currentPolicy'](newVal, oldVal) {
      if (newVal.contractId !== oldVal.contractId) {
        this.globalStore.removeAlerts('payment');
      }
    },
  },
  mounted() {
    const scrollToLocation = this.policyStore.scrollToPolicyDetails;
    if (this?.$route?.name === RouteNames.GUIDANCE_CALCULATOR) {
      this.activePolicyTab = 1;
    }
    if (this?.$route?.name === RouteNames.TERM_LIFE_BENEFITS) {
      this.activePolicyTab = 2;
    }
    // Clearing out the Alerts for payment when they come in here
    this.globalStore.removeAlerts('payment');

    if (scrollToLocation) {
      if (scrollToLocation === 'policy-panel') {
        window.scrollTo(0, 0);
      } else {
        // Everytime there is a change in size or reordering of cards the following needs to be looked at
        window.setTimeout(() => {
          // Add Child is the first section (coverage) so we don't have to worry about it
          if (scrollToLocation === 'contact_info') {
            if (this.$vuetify.display.lgAndUp) {
              window.scrollTo(0, 920);
            } else if (this.$vuetify.display.md || this.$vuetify.display.sm) {
              window.scrollTo(0, 920);
            } else {
              window.scrollTo(0, 1260);
            }
          } else if (scrollToLocation === 'beneficiary') {
            if (this.$vuetify.display.lgAndUp) {
              window.scrollTo(0, 1200);
            } else if (this.$vuetify.display.md || this.$vuetify.display.sm) {
              window.scrollTo(0, 1200);
            } else {
              window.scrollTo(0, 1670);
            }
          } else if (scrollToLocation === 'payment') {
            // Stick with this
            if (this.$vuetify.display.lgAndUp) {
              window.scrollTo(0, 390);
            } else if (this.$vuetify.display.md || this.$vuetify.display.sm) {
              window.scrollTo(0, 390);
            } else {
              window.scrollTo(0, 470);
            }
          }
        }, 200);
      }
    }
  },
  unmounted() {
    this.policyStore.scrollToPolicyDetails = 'policy-panel';
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.v-icon.guidance-icon {
  background-color: rgba(0, 0, 0, 0) !important;
  height: 24px;
  width: 24px;
  background-image: url(./assets/guidance-icon.svg);

  &.white {
    background-image: url(./assets/guidance-icon-white.svg);
  }
}

:deep(.v-tabs__bar) {
  background-color: transparent !important;
  box-shadow: none;
  z-index: 2;

  > .v-tabs__wrapper {
    > .v-tabs__container {
      width: 100%;

      @media only screen and (max-width: 767px) {
        height: 64px;
      }
    }
  }
}

:deep(div.v-tabs__div) {
  width: 33.3333333333% !important;
}

@media (min-width: 1200px) {
  .card-slide-container {
    position: relative;
    overflow: hidden !important;
  }

  .card-slide-item {
    perspective: 500px;
    transition: all 0.5s;
    position: absolute;

    &:last-child {
      position: relative;
    }

    &.last-cards {
      filter: blur(3px);
      transform: translate3d(100px, 0, 0) scaleX(0.8) scaleY(0.8);
      opacity: 0;
    }
  }

  .card-slide-enter,
  .card-slide-leave-to {
    transform: translateX(-100%);
    opacity: 0;
  }

  .card-slide-container {
    perspective-origin: 0 0;
  }
}

@media (max-width: 767px) {
  .card-slide-container {
    perspective-origin: 50% 0;
  }

  .card-slide-item {
    &.card-1 {
      transform: translate3d(0, -15px, -20px);
    }

    &.card-2 {
      transform: translate3d(0, -30px, -40px);
    }

    &.last-cards {
      transform: translate3d(0, -45px, -60px);
    }
  }
}

@media (max-width: 1024px) {
  .card-slide-item:not(:last-child) {
    display: none;
  }
}

.policy-alert {
  :deep(.v-alert) {
    margin-top: 0;
    margin-bottom: 0;
  }
}
</style>
